import {
  FilterType,
  SelectedFilter,
  SelectedFilters,
} from '@/modules/queryfilter/domain/interfaces';
import serializeFilters from '@/services/query/serializeFilters';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';

import {
  DataClicked,
  DataContent,
  DataSubcomponent,
  useModalResponsiveTableRequestContext,
} from '.';
import SearchBar from '../ResponsiveList/components/SearchBar';
import TableRequest from '../ResponsiveTableV2/components/SubComponent/types/TableRequest';

interface TabType {
  id: string | number;
  label: string;
}

export const ModalResponsiveTableRequest: React.FC = () => {
  const [searchText, setSearchText] = React.useState<string>('');
  const {
    isOpen,
    handleClose,
    modalSize,
    infoSubComponent,
    cancelToken,
  } = useModalResponsiveTableRequestContext();

  const {
    dataClicked = {} as DataClicked,
    dataSubcomponent = {} as DataSubcomponent,
    dataContent = {} as DataContent,
  } = infoSubComponent;

  const {
    lever_title,
    lever_filter,
    table_filter,
    modal_trigger,
    data = {},
    additional_info_title,
  } = dataSubcomponent;
  const { indexBy } = dataContent;
  const { id, value: title, indexValue, value } = dataClicked;

  const parsedLeverToTabs: TabType[] = React.useMemo(() => {
    if (!lever_title) return [];
    return lever_title?.map((item: string, index: number) => ({
      id: index,
      label: item,
    }));
  }, [lever_title]);

  const [filters, setFilters] = React.useState<SelectedFilters>();
  const [formattedFilter, setFormattedFilter] = React.useState<string>();

  useEffect(() => {
    if (
      table_filter &&
      lever_filter &&
      modal_trigger &&
      indexBy &&
      indexValue
    ) {
      const customFilter = indexValue
        ? [
            {
              column: indexBy,
              selected: [indexValue],
              type: FilterType.MULTI,
            },
          ]
        : ([] as SelectedFilter[]);
      setFilters({
        table: table_filter,
        filters: [
          ...customFilter,
          { column: lever_filter, selected: true, type: FilterType.SINGLE },
          {
            column: modal_trigger,
            selected: [id || value || title],
            type: FilterType.MULTI,
          },
        ],
      });
    } else if (
      table_filter &&
      modal_trigger
    ) {
      const customFilter = indexValue
        ? [
            {
              column: indexBy,
              selected: [indexValue],
              type: FilterType.MULTI,
            },
          ]
        : ([] as SelectedFilter[]);
      setFilters({
        table: table_filter,
        filters: [
          ...customFilter,
          {
            column: modal_trigger,
            selected: [id || value || title],
            type: FilterType.MULTI,
          },
        ],
      });
    }
  }, [
    id,
    indexBy,
    lever_filter,
    modal_trigger,
    table_filter,
    title,
    data,
    indexValue,
    value,
  ]);

  useEffect(() => {
    if (filters && !searchText) {
      setFormattedFilter(
        serializeFilters({
          table: filters.table,
          filters: [...filters.filters],
        }),
      );
    }
    if (searchText && filters) {
      setFormattedFilter(
        serializeFilters({
          table: filters.table,
          filters: [
            ...filters.filters,
            { column: 'search', selected: searchText, type: FilterType.SEARCH },
          ],
        }),
      );
    }
  }, [searchText, filters]);

  useEffect(() => {
    if (!isOpen) {
      setSearchText('');
    }
  }, [isOpen]);

  const aditionalInfo = additional_info_title || indexValue

  return (
    isOpen && (
      <Modal
        size={modalSize}
        isOpen={isOpen}
        onClose={handleClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent minH="300px">
          <ModalHeader>
            {id || value || title}{aditionalInfo && ` - ${aditionalInfo}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SearchBar
              placeholder="Buscar por qualquer atributo das colunas..."
              defaultValue={searchText}
              onChange={(value: string) => setSearchText(value)}
            />

            {parsedLeverToTabs.length > 0 && (
              <Tabs
                onChange={(tab) => {
                  if (parsedLeverToTabs[tab].label.includes('com')) {
                    setFilters((prev) => ({
                      ...prev,
                      filters: [
                        ...prev?.filters.filter(
                          (filter) => filter.column !== lever_filter,
                        ),
                        {
                          column: lever_filter,
                          selected: true,
                          type: FilterType.SINGLE,
                        },
                      ],
                    }));
                  } else {
                    setFilters((prev) => ({
                      ...prev,
                      filters: [
                        ...prev?.filters.filter(
                          (filter) => filter.column !== lever_filter,
                        ),
                        {
                          column: lever_filter,
                          selected: false,
                          type: FilterType.SINGLE,
                        },
                      ],
                    }));
                  }
                }}
                lazyBehavior="unmount"
              >
                <TabList>
                  {parsedLeverToTabs.map((tab) => (
                    <Tab key={tab.id}>{tab.label}</Tab>
                  ))}
                </TabList>
                <TabPanels>
                  {parsedLeverToTabs.map((tab) => (
                    <TabPanel key={tab.id}>
                      {formattedFilter && (
                        <TableRequest
                          cancelToken={cancelToken}
                          data={{ ...data, minHeight: '40dvh', maxHeight: '65vh' }}
                          filters={formattedFilter}
                        />
                      )}
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    )
  );
};
